import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import { distinctUntilChanged, finalize, fromEvent, map, Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ALLOWED_MANUAL_UPLOAD, ALLOWED_RESEND, ALLOWED_SEND, ALLOWED_VOID, RED_STATUS } from '../../constants/docusign-constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Loading } from '@lmc-app/applications/loading';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { ConfirmDocusignDialogComponent } from '../../confirm-docusign-dialog/confirm-docusign-dialog.component';
import { AgreementsVoidComponent } from '../agreements-void/agreements-void.component';
import { callLoader } from '../../helper-functions/docusign-utils';

interface noteState {
  subject: string;
  description: string;
  notes: string;
}

interface VoidBody{
  envelopeId:string;
  userInfo: User;
  application: any;
  noteDetails: noteState;
}

@Component({
  selector: 'app-agreements-list',
  templateUrl: './agreements-list.component.html',
  styleUrls: ['./agreements-list.component.scss']
})
export class AgreementsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly _listenerService = inject(ListenerService);
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() appData: any;
  @Input() application: any;
  @Input() currentUser: User; 
  @Output() applicationChange = new EventEmitter();
  @Output() createAgreement = new EventEmitter<void>();   // Create button, parent opens the recipients modal

  readonly allowSend = ALLOWED_SEND;  
  readonly allowResend = ALLOWED_RESEND;    
  readonly allowManualUpload = ALLOWED_MANUAL_UPLOAD;      
  readonly allowVoid = ALLOWED_VOID;       
  readonly redStatus = RED_STATUS;   
  private readonly _dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _bottomSheet = inject(MatBottomSheet);

  
  envelopeData: any[] = [];
  hasEnvelopes: boolean = false;
  isLoading: boolean = false;
  readonly tableHeaders: string[] = ['applicantName', 'applicantType', 'clientEmail', 'templateName', 'status', 'createdBy', 'createdAt', 'sentBy', 'sentAt', 'actions'];
  
  selectedEnvelopeData:any;
  showAgreementList: boolean = true;
  isScrolled = false;
  selectedRowData: any = null;

  constructor(
    private _docusignService: DocusignService,
    private _agreementVoidDialog: MatDialog,
  ){}

  ngOnInit(): void {
    this._listenerService.on<boolean>('agreementsListTabSelected').pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this.showAgreementList = true;
      this.selectedEnvelopeData = null;
      this.selectedRowData = null;
      // Always fetch the latest envelopes when Agreements is clicked
      if (this.application?.id) {
        this.getAgreementEnvelopes();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
      if(changes.application?.currentValue){
        const previousApplicationId = changes.application.previousValue?.id;
        const newApplicationId = changes.application.currentValue?.id;
        if (previousApplicationId && previousApplicationId !== newApplicationId) {
          // Close any envelope preview left open from the previous application
          this.showAgreementList = true;
          this.selectedEnvelopeData = null;
          this.selectedRowData = null;
        }

        this.application = changes.application?.currentValue;
        this.getAgreementEnvelopes();
      }
      
      if (changes.currentUser?.currentValue) {
        this.currentUser = changes.currentUser?.currentValue;
      }
      
      if (changes.appData?.currentValue) {
        this.appData = changes.appData?.currentValue;
      }
    }

  ngAfterViewInit(): void {
    // Show the header bar shadow only once the summary content has scrolled
    const scrollContainer = this._elementRef.nativeElement.closest('.scrollable-content');
    if (!scrollContainer) {
      return;
    }
    fromEvent(scrollContainer, 'scroll', { passive: true })
      .pipe(
        map(() => scrollContainer.scrollTop > 0),
        distinctUntilChanged(),
        takeUntil(this._destroy$)
      )
      .subscribe((isScrolled) => (this.isScrolled = isScrolled));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  getAgreementEnvelopes(){
    this.isLoading = true;
    this._docusignService.getAgreementEnvelopes(this.application?.id,'AGREEMENT').pipe(
      finalize(() => this.isLoading = false),
      takeUntil(this._destroy$)
    ).subscribe({
      next: (envelopes) => {
        this.envelopeData = envelopes || [];
        this.hasEnvelopes = !!this.envelopeData.length;
      }
    });
  }

  onRefresh(){    
    this.getAgreementEnvelopes();
  }

  openPreviewWindow(element:any){ 
    this.showAgreementList = false;
    this.selectedEnvelopeData = { 
      application: this.application,
      envelope_id: element?.envelope_id, 
      currentUser: this.currentUser,
      rowData: element
    }
  }
  
  // Call this when a row is clicked
  onRowClick(row: any) {
    this.selectedRowData = row;
  }

  sendEnvelopeAction(selectedActionRow:Event){
    const sendEnvelopeName = selectedActionRow?.['templateRowId']?.['template_name']?.replaceAll('_', ' ');
    this._dialog.open(ConfirmDocusignDialogComponent, {
      data: {
        title:`Do you want to send ${sendEnvelopeName}?`,
        type: "",
        fileName: "",
      },
    })
    .afterClosed()
    .pipe(takeUntil(this._destroy$))
    .subscribe((result) => {
      if(result){
        this.callLoader('Sending envelope');
        const isEdited = !!selectedActionRow?.['modified_at'];
        this._docusignService.sendEnvelope(
          {
            envelope_id: selectedActionRow?.['envelope_id'],
            envelopeStatus: selectedActionRow?.['statusId']?.['status_name'],
            envelopeData: selectedActionRow?.['evelopeDetails'],
            envelopeIsEdited: isEdited,
            userInfo: this.currentUser}).pipe(
          finalize(() => {
            this._bottomSheet.dismiss();
          }),
          takeUntil(this._destroy$)
        ).subscribe({
          next: (response)=>{
            if(response?.status === 200){
              this.openSnackBar('success',response?.message);
              this.getAgreementEnvelopes();
            } else{
              this.openSnackBar('error',`Send error:  ${response?.message}`);
            }
          }
        })
      }
    });    
  }

  uploadToWorkdrive(rowData:any){
    this.callLoader('Uploading to workdrive');
    this._docusignService?.uploadCompletedDocusign({folderId: this.application?.zohoworkdriveforcrm__Workdrive_Folder_ID, envelopeId: rowData?.envelope_id}).pipe(
      takeUntil(this._destroy$)
    ).subscribe({
      next: (response) => {
        this._bottomSheet.dismiss();
      }
    })
  }

  callLoader(customNessage: string){
    this._bottomSheet.open(Loading, {
      disableClose: true,
      data: { message: customNessage },
    });
  }

  openSnackBar(messageType: string, message: string){
    if(messageType === 'error'){
      return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_ALIVE,);
    } else{
      return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_SHORT,);
    }
  }

  openVoidAction(element:any){ 
    const dialogRef = this._agreementVoidDialog.open(AgreementsVoidComponent, {
      maxHeight: '90vh',
      width: '800px',
      disableClose: false,
      panelClass: 'hide-dialog-scrollbar',
      data: { 
        envelopeId: element?.envelope_id
      }
    });
    dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe(result => {
      if(result){
        const ref = callLoader(this._bottomSheet, 'Voiding Envelope');
        const voidEnvelopeInfo: VoidBody = {
          envelopeId: result?.envelopeId,
          userInfo: this.currentUser,
          application: this.appData,
          noteDetails: { subject: 'Voided Agreement', description: '-', notes: result?.reason }
        };
        this._docusignService.voidEnvelope(voidEnvelopeInfo).pipe(
            finalize(() => {
              ref.dismiss();
            }),
            takeUntil(this._destroy$)
          ).subscribe({
            next: (response)=>{
              if(response){
                this.openSnackBar('voided',`'Voided: ${response?.envelopeId}`);
                this.getAgreementEnvelopes();
                this.applicationChange.emit(this.application);
              } else{
                this.openSnackBar('error',`Error: ENVELOPE_CANNOT_VOID_INVALID_STATE`);
              }
            }
          })
        }        
      }
    );
  }

  receiveClosedMessage($event: boolean) {
    this.showAgreementList = $event;
    // Reload so status/envelope changes made in the preview show up in the list
    if ($event) {
      this.getAgreementEnvelopes();
    }
  }

  deleteEnvelopeAction(selectedRow:any){
    const ref = callLoader(this._bottomSheet, 'Deleting agreement');
      this._docusignService.deleteEnvelope({envelopeData: selectedRow, userInfo: this.currentUser, docusignType: 'AGREEMENT'}).pipe(
          finalize(() => {
            ref.dismiss();
          }),
          takeUntil(this._destroy$)
        ).subscribe({
          next: (response)=>{
            if(response?.status === 201 || response?.status === 200){   
              this.getAgreementEnvelopes();
              this.openSnackBar('success',response?.message);   
            }else{
              this.openSnackBar('error',response)
            } 
          }
        })
      }

  onApplicationChanged(application:any) {
    this.applicationChange.emit(application);
  }

}
